import React, { useRef, useState } from "react";
import { useTranslation } from "react-i18next";

import { uploadAttachment, UploadedAttachment } from "../../storageUpload";

interface AttachmentUploaderProps {
  fieldPath: string;
  label: string;
  attachments: UploadedAttachment[];
  onUploaded: (nextAttachments: UploadedAttachment[]) => void;
  max?: number;
}

/**
 * Minimal uploader for a document's `attachments` array
 * (data.documents.<type>.attachments, max 5 — document_attachments_schema).
 * api-pd.id.express's complete endpoint already persists the updated array
 * server-side; onUploaded only syncs the form's local state for immediate
 * display, it is not itself a save.
 */
const AttachmentUploader: React.FC<AttachmentUploaderProps> = ({
  fieldPath,
  label,
  attachments,
  onUploaded,
  max = 5,
}) => {
  const { t } = useTranslation();
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;

    setError(null);
    setUploading(true);
    try {
      const attachment = await uploadAttachment(file, "kyc_document", fieldPath);
      onUploaded([...attachments, attachment].slice(-max));
    } catch (err) {
      console.error("Attachment upload failed", err);
      setError(t("personalData.errors.attachmentUploadFailed", { defaultValue: "Could not upload the file. Please try again." }));
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="pd-field pd-attachment-uploader">
      <label className="pd-field__label">
        <span>{label}</span>
      </label>
      <ul className="pd-attachment-uploader__list">
        {attachments.map((attachment) => (
          <li key={attachment.fileId}>
            {attachment.url ? (
              <a href={attachment.url} target="_blank" rel="noreferrer">{attachment.fileName}</a>
            ) : (
              <span>{attachment.fileName}</span>
            )}
          </li>
        ))}
      </ul>
      <button
        type="button"
        className="pd-secondary-button"
        disabled={uploading || attachments.length >= max}
        onClick={() => inputRef.current?.click()}
      >
        {uploading
          ? t("personalData.ui.uploading", { defaultValue: "Uploading..." })
          : t("personalData.actions.addAttachment", { defaultValue: "Add file" })}
      </button>
      <input
        ref={inputRef}
        type="file"
        accept="application/pdf,image/jpeg,image/png,image/webp"
        hidden
        onChange={handleFileChange}
      />
      <span className="pd-attachment-uploader__count">{attachments.length}/{max}</span>
      {error && <p className="pd-attachment-uploader__error">{error}</p>}
    </div>
  );
};

export default AttachmentUploader;
